import { canonicalizeCalculatorFishingSourceV1 } from "./calculator-fishing-source.js";

const U8_MAX = 255;
const U16_MAX = 65_535;
const U32_MAX = 4_294_967_295;

const MODEL_SELECTION_FIELDS = Object.freeze([
    "fishingSource",
    "supplementalItemIds",
    "equipmentOptionIds",
    "petSelections",
    "tradeOriginId",
    "tradeDestinationId",
]);
const MODEL_PET_SELECTION_FIELDS = Object.freeze([
    "petId",
    "tier",
    "specialOptionId",
    "talentOptionId",
    "learnedSkillOptionIds",
]);
const SCENARIO_FIELDS = Object.freeze([
    "schemaVersion",
    "fishingSource",
    "fishingLevel",
    "lifeskillLevel",
    "mastery",
    "rateAlternativeOverrides",
    "resourcesPercent",
    "equipment",
    "pets",
    "trade",
    "session",
    "target",
    "discardGrade",
    "branded",
]);
const EQUIPMENT_FIELDS = Object.freeze([
    "rodId",
    "floatId",
    "chairId",
    "lightstoneSetId",
    "backpackId",
    "outfitIds",
    "foodIds",
    "buffIds",
]);
const PET_FIELDS = Object.freeze([
    "petId",
    "tier",
    "packLeader",
    "specialOptionId",
    "talentOptionId",
    "learnedSkillOptionIds",
]);
const CATALOG_PET_V2_FIELDS = Object.freeze([
    "kind",
    "petId",
    "tier",
    "packLeader",
    "specialOptionId",
    "talentOptionId",
    "learnedSkillOptionIds",
]);
const CUSTOM_PET_V2_FIELDS = Object.freeze([
    "kind",
    "displayLabel",
    "basedOn",
    "tier",
    "packLeader",
    "specialOptionId",
    "talentOptionId",
    "learnedSkillOptionIds",
]);
const TRADE_FIELDS = Object.freeze([
    "tradeLevel",
    "originId",
    "destination",
    "priceCurvePercent",
    "applyModifiers",
]);
const SESSION_FIELDS = Object.freeze([
    "activeFishing",
    "activeCatchTimeSeconds",
    "afkCatchTimeSeconds",
    "timespanAmount",
    "timespanUnit",
]);
const TARGET_FIELDS = Object.freeze(["itemId", "amount", "pmfTailCount"]);
const TIMESPAN_UNITS = new Set(["minutes", "hours", "days", "weeks"]);
const DISCARD_GRADES = new Set(["none", "white", "green", "blue", "yellow"]);

function requireArray(value, path, contract) {
    if (!Array.isArray(value)) {
        contract.fail(path, "expected an array");
    }
    return value;
}

function requireBoolean(value, path, contract) {
    if (typeof value !== "boolean") {
        contract.fail(path, "expected a boolean");
    }
    return value;
}

function requireIdentifier(value, path, contract) {
    if (typeof value !== "string" || !value || value.trim() !== value) {
        contract.fail(path, "expected a non-empty identifier without surrounding whitespace");
    }
    return value;
}

function requireNullableIdentifier(value, path, contract) {
    return value === null ? null : requireIdentifier(value, path, contract);
}

function requireInteger(value, path, maximum, contract, minimum = 0) {
    if (!Number.isSafeInteger(value) || value < minimum || value > maximum) {
        contract.fail(path, `expected an integer from ${minimum} through ${maximum}`);
    }
    return value;
}

function requireFiniteNonNegative(value, path, contract) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        contract.fail(path, "expected a finite non-negative number");
    }
    return value;
}

function validateIdentifierArray(value, path, contract) {
    return requireArray(value, path, contract).forEach((entry, index) => {
        requireIdentifier(entry, `${path}[${index}]`, contract);
    });
}

function validateRateAlternativeOverrides(value, path, contract) {
    const familyIds = new Set();
    requireArray(value, path, contract).forEach((entry, index) => {
        const entryPath = `${path}[${index}]`;
        const override = contract.requireExactFields(
            entry,
            ["familyId", "alternativeId"],
            entryPath,
        );
        const familyId = requireIdentifier(
            override.familyId,
            `${entryPath}.familyId`,
            contract,
        );
        requireIdentifier(
            override.alternativeId,
            `${entryPath}.alternativeId`,
            contract,
        );
        if (familyIds.has(familyId)) {
            contract.fail(`${entryPath}.familyId`, "expected at most one override per family");
        }
        familyIds.add(familyId);
    });
}

function validateModelPetSelection(value, path, contract) {
    const pet = contract.requireExactFields(value, MODEL_PET_SELECTION_FIELDS, path);
    requireIdentifier(pet.petId, `${path}.petId`, contract);
    requireInteger(pet.tier, `${path}.tier`, U8_MAX, contract);
    requireNullableIdentifier(pet.specialOptionId, `${path}.specialOptionId`, contract);
    requireNullableIdentifier(pet.talentOptionId, `${path}.talentOptionId`, contract);
    validateIdentifierArray(
        pet.learnedSkillOptionIds,
        `${path}.learnedSkillOptionIds`,
        contract,
    );
}

function validatePetSelection(value, path, contract) {
    const pet = contract.requireExactFields(value, PET_FIELDS, path);
    requireIdentifier(pet.petId, `${path}.petId`, contract);
    requireInteger(pet.tier, `${path}.tier`, U8_MAX, contract);
    requireBoolean(pet.packLeader, `${path}.packLeader`, contract);
    requireNullableIdentifier(pet.specialOptionId, `${path}.specialOptionId`, contract);
    requireNullableIdentifier(pet.talentOptionId, `${path}.talentOptionId`, contract);
    validateIdentifierArray(
        pet.learnedSkillOptionIds,
        `${path}.learnedSkillOptionIds`,
        contract,
    );
}

function validatePetSelectionV2(value, path, contract) {
    const fields = value?.kind === "catalog"
        ? CATALOG_PET_V2_FIELDS
        : CUSTOM_PET_V2_FIELDS;
    const pet = contract.requireExactFields(value, fields, path);
    if (pet.kind === "catalog") {
        requireIdentifier(pet.petId, `${path}.petId`, contract);
    } else if (pet.kind === "custom") {
        if (pet.displayLabel !== null) {
            requireIdentifier(pet.displayLabel, `${path}.displayLabel`, contract);
        }
        requireNullableIdentifier(pet.basedOn, `${path}.basedOn`, contract);
    } else {
        contract.fail(`${path}.kind`, "expected catalog or custom");
    }
    requireInteger(pet.tier, `${path}.tier`, 5, contract, 1);
    requireBoolean(pet.packLeader, `${path}.packLeader`, contract);
    requireNullableIdentifier(pet.specialOptionId, `${path}.specialOptionId`, contract);
    requireNullableIdentifier(pet.talentOptionId, `${path}.talentOptionId`, contract);
    validateIdentifierArray(
        pet.learnedSkillOptionIds,
        `${path}.learnedSkillOptionIds`,
        contract,
    );
    return pet;
}

export function decodeCalculatorScenarioV1ToV2(value, path, contract) {
    validateCalculatorScenarioV1(value, path, contract);
    return {
        ...value,
        pets: value.pets.map((pet) => ({
            kind: "catalog",
            petId: pet.petId,
            tier: pet.tier,
            packLeader: pet.packLeader,
            specialOptionId: pet.specialOptionId,
            talentOptionId: pet.talentOptionId,
            learnedSkillOptionIds: [...pet.learnedSkillOptionIds],
        })),
    };
}

export function validateCalculatorScenarioV2(value, path, contract) {
    return validateCalculatorScenario(value, path, contract, validatePetSelectionV2);
}

function validateTradeDestination(value, path, contract) {
    if (value?.kind === "catalog") {
        const destination = contract.requireExactFields(
            value,
            ["kind", "destinationId"],
            path,
        );
        requireIdentifier(destination.destinationId, `${path}.destinationId`, contract);
        return;
    }
    if (value?.kind === "custom") {
        const destination = contract.requireExactFields(
            value,
            ["kind", "distanceBonusPercent"],
            path,
        );
        requireFiniteNonNegative(
            destination.distanceBonusPercent,
            `${path}.distanceBonusPercent`,
            contract,
        );
        return;
    }
    contract.requireExactFields(value, ["kind"], path);
    contract.fail(`${path}.kind`, "expected catalog or custom");
}

export function validateCalculatorModelSelectionV1(value, path, contract) {
    const selection = contract.requireExactFields(value, MODEL_SELECTION_FIELDS, path);
    canonicalizeCalculatorFishingSourceV1(
        selection.fishingSource,
        `${path}.fishingSource`,
        (issuePath, _code, message) => contract.fail(issuePath, message),
    );
    requireArray(selection.supplementalItemIds, `${path}.supplementalItemIds`, contract)
        .forEach((itemId, index) => requireInteger(
            itemId,
            `${path}.supplementalItemIds[${index}]`,
            U32_MAX,
            contract,
            1,
        ));
    validateIdentifierArray(selection.equipmentOptionIds, `${path}.equipmentOptionIds`, contract);
    requireArray(selection.petSelections, `${path}.petSelections`, contract)
        .forEach((pet, index) => validateModelPetSelection(
            pet,
            `${path}.petSelections[${index}]`,
            contract,
        ));
    requireNullableIdentifier(selection.tradeOriginId, `${path}.tradeOriginId`, contract);
    requireNullableIdentifier(
        selection.tradeDestinationId,
        `${path}.tradeDestinationId`,
        contract,
    );
    return selection;
}

export function validateCalculatorScenarioV1(value, path, contract) {
    return validateCalculatorScenario(value, path, contract, validatePetSelection);
}

function validateCalculatorScenario(value, path, contract, validatePet) {
    const scenario = contract.requireExactFields(value, SCENARIO_FIELDS, path);
    if (scenario.schemaVersion !== 1) {
        contract.fail(`${path}.schemaVersion`, "expected exactly 1");
    }
    canonicalizeCalculatorFishingSourceV1(
        scenario.fishingSource,
        `${path}.fishingSource`,
        (issuePath, _code, message) => contract.fail(issuePath, message),
    );
    requireInteger(scenario.fishingLevel, `${path}.fishingLevel`, U8_MAX, contract);
    requireInteger(scenario.lifeskillLevel, `${path}.lifeskillLevel`, U16_MAX, contract);
    requireInteger(scenario.mastery, `${path}.mastery`, U32_MAX, contract);
    validateRateAlternativeOverrides(
        scenario.rateAlternativeOverrides,
        `${path}.rateAlternativeOverrides`,
        contract,
    );
    requireFiniteNonNegative(scenario.resourcesPercent, `${path}.resourcesPercent`, contract);

    const equipment = contract.requireExactFields(
        scenario.equipment,
        EQUIPMENT_FIELDS,
        `${path}.equipment`,
    );
    requireIdentifier(equipment.rodId, `${path}.equipment.rodId`, contract);
    for (const field of ["floatId", "chairId", "lightstoneSetId", "backpackId"]) {
        requireNullableIdentifier(equipment[field], `${path}.equipment.${field}`, contract);
    }
    for (const field of ["outfitIds", "foodIds", "buffIds"]) {
        validateIdentifierArray(equipment[field], `${path}.equipment.${field}`, contract);
    }

    requireArray(scenario.pets, `${path}.pets`, contract).forEach((pet, index) => {
        validatePet(pet, `${path}.pets[${index}]`, contract);
    });

    const trade = contract.requireExactFields(scenario.trade, TRADE_FIELDS, `${path}.trade`);
    requireInteger(trade.tradeLevel, `${path}.trade.tradeLevel`, U16_MAX, contract);
    requireNullableIdentifier(trade.originId, `${path}.trade.originId`, contract);
    validateTradeDestination(trade.destination, `${path}.trade.destination`, contract);
    requireFiniteNonNegative(
        trade.priceCurvePercent,
        `${path}.trade.priceCurvePercent`,
        contract,
    );
    requireBoolean(trade.applyModifiers, `${path}.trade.applyModifiers`, contract);

    const session = contract.requireExactFields(
        scenario.session,
        SESSION_FIELDS,
        `${path}.session`,
    );
    requireBoolean(session.activeFishing, `${path}.session.activeFishing`, contract);
    for (const field of ["activeCatchTimeSeconds", "afkCatchTimeSeconds", "timespanAmount"]) {
        requireFiniteNonNegative(session[field], `${path}.session.${field}`, contract);
    }
    if (!TIMESPAN_UNITS.has(session.timespanUnit)) {
        contract.fail(`${path}.session.timespanUnit`, "unsupported timespan unit");
    }

    const target = contract.requireExactFields(scenario.target, TARGET_FIELDS, `${path}.target`);
    if (target.itemId !== null) {
        requireInteger(target.itemId, `${path}.target.itemId`, U32_MAX, contract, 1);
    }
    requireInteger(target.amount, `${path}.target.amount`, U32_MAX, contract);
    if (target.pmfTailCount !== null) {
        requireInteger(target.pmfTailCount, `${path}.target.pmfTailCount`, U32_MAX, contract);
    }
    if (!DISCARD_GRADES.has(scenario.discardGrade)) {
        contract.fail(`${path}.discardGrade`, "unsupported discard grade");
    }
    requireBoolean(scenario.branded, `${path}.branded`, contract);
    return scenario;
}
